<template>
  <transition name="slide-up">
    <div v-if="showInstallPrompt" class="pwa-install-banner">
      <div class="install-content">
        <div class="install-icon">
          <fa-icon :icon="['fas', 'mobile-alt']" />
        </div>
        <div class="install-text">
          <h4>Install Publisher App</h4>
          <p v-if="!isIos">Get the best experience with our app</p>
          <p v-else>
            Tap <strong>Share</strong> → <strong>Add to Home Screen</strong>
          </p>
        </div>
        <div class="install-actions">
          <button v-if="!isIos" @click="installPwa" class="btn-install">Install</button>
          <button @click="dismissPrompt" class="btn-dismiss" aria-label="Dismiss install">
            <fa-icon :icon="['fas', 'times']" />
          </button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
export default {
  name: 'PwaInstallPrompt',
  data() {
    return {
      showInstallPrompt: false,
    };
  },
  computed: {
    deferredPrompt() {
      return this.$store.getters.pwaInstallPrompt;
    },
    isIos() {
      const ua = window.navigator.userAgent;
      const isIOSDevice = /iPad|iPhone|iPod/.test(ua);
      const isMacTouch = /Macintosh/.test(ua) && 'ontouchend' in document;
      return isIOSDevice || isMacTouch;
    },
    isStandalone() {
      return window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
    }
  },
  watch: {
    deferredPrompt(newPrompt) {
      // Android/Chrome style prompt
      if (!this.isIos && newPrompt && !this.isInstallDismissed()) {
        this.showInstallPrompt = true;
      }
    },
  },
  mounted() {
    // Hide if already installed
    if (this.isStandalone) {
      this.showInstallPrompt = false;
      return;
    }

    // Respect dismissal cooldown
    if (this.isInstallDismissed()) {
      this.showInstallPrompt = false;
      return;
    }

    // iOS: show guidance banner since beforeinstallprompt isn't supported
    if (this.isIos) {
      this.showInstallPrompt = true;
      return;
    }

    // Android/Chrome: show when event available
    if (this.deferredPrompt) {
      this.showInstallPrompt = true;
    }
  },
  methods: {
    async installPwa() {
      if (!this.deferredPrompt) {
        return;
      }

      this.deferredPrompt.prompt();
      
      const { outcome } = await this.deferredPrompt.userChoice;
      console.log(`User response to the install prompt: ${outcome}`);
      
      this.$store.commit('setPwaInstallPrompt', null);
      this.showInstallPrompt = false;
      
      if (outcome === 'accepted') {
        localStorage.setItem('pwa-installed', 'true');
      }
    },
    dismissPrompt() {
      this.showInstallPrompt = false;
      localStorage.setItem('pwa-install-dismissed', Date.now().toString());
    },
    isInstallDismissed() {
      const dismissed = localStorage.getItem('pwa-install-dismissed');
      if (!dismissed) return false;
      
      // Show again after 7 days
      const dismissedTime = parseInt(dismissed);
      const sevenDaysInMs = 7 * 24 * 60 * 60 * 1000;
      return (Date.now() - dismissedTime) < sevenDaysInMs;
    },
  },
};
</script>

<style scoped>
.pwa-install-banner {
  position: fixed;
  bottom: 80px;
  left: 0;
  right: 0;
  z-index: 1001;
  padding: 0 16px;
}

@media (min-width: 768px) {
  .pwa-install-banner {
    bottom: 20px;
    left: 50%;
    transform: translateX(-50%);
    max-width: 500px;
    padding: 0;
  }
}

.install-content {
  background: linear-gradient(135deg, #1e40af 0%, #3b82f6 100%);
  color: white;
  border-radius: 12px;
  padding: 16px;
  display: flex;
  align-items: center;
  gap: 16px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
}

.install-icon {
  font-size: 32px;
  flex-shrink: 0;
}

.install-text {
  flex: 1;
  min-width: 0;
}

.install-text h4 {
  margin: 0 0 4px 0;
  font-size: 16px;
  font-weight: 600;
}

.install-text p {
  margin: 0;
  font-size: 13px;
  opacity: 0.9;
}

.install-actions {
  display: flex;
  gap: 8px;
  align-items: center;
  flex-shrink: 0;
}

.btn-install {
  background: white;
  color: #1e40af;
  border: none;
  padding: 8px 20px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
}

.btn-install:hover {
  transform: scale(1.05);
  box-shadow: 0 4px 12px rgba(255, 255, 255, 0.3);
}

.btn-install:active {
  transform: scale(0.98);
}

.btn-dismiss {
  background: rgba(255, 255, 255, 0.2);
  color: white;
  border: none;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.btn-dismiss:hover {
  background: rgba(255, 255, 255, 0.3);
}

/* Transitions */
.slide-up-enter-active {
  transition: all 0.3s ease;
}

.slide-up-leave-active {
  transition: all 0.2s ease;
}

.slide-up-enter {
  transform: translateY(100%);
  opacity: 0;
}

.slide-up-leave-to {
  transform: translateY(100%);
  opacity: 0;
}

/* Dark Mode */
:global(.dark) .install-content {
  background: linear-gradient(135deg, #1e3a8a 0%, #2563eb 100%);
}
</style>
